import type { SubjectTypeDefinitionFetchFailedAction } from '@Application/SubjectTypeDefinitions/Actions/subjectTypeDefinitionFetchFailed';
import FetchSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeDefinition';
import fetchSubjectTypeDefinition from '@Application/SubjectTypeDefinitions/fetchSubjectTypeDefinition';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import useSubjectTypeEditorReferences from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Hooks/useSubjectTypeEditorReferences';
import SubjectTypeEditor from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/SubjectTypeEditor';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { SearchX } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';

const LIST_PATH = '/administration/subject-types';
const DEFAULT_LOAD_ERROR_MESSAGE = 'Une erreur est survenue pendant le chargement du type de dossier.';

const skeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

type FetchFailure = {
	readonly subjectTypeId: number;
	readonly isNotFound: boolean;
	readonly message: string | null;
};

/**
 * Detects the `SUBJECT_TYPE_DEFINITION_FETCH_FAILED` action that `dispatch(...)` rejects with: its
 * `status` and `message` would be lost if `useStoreQuery` re-wrapped it.
 */
const isFetchFailure = (candidate: unknown): candidate is SubjectTypeDefinitionFetchFailedAction => {
	return (
		typeof candidate === 'object' &&
		candidate !== null &&
		'type' in candidate &&
		candidate.type === 'SUBJECT_TYPE_DEFINITION_FETCH_FAILED'
	);
};

/**
 * Resolves what the editor needs before mounting it: the type addressed by the route (none for a new
 * type) and the references the sections read from the store. The editor is mounted only once the type
 * is in the store. The editor opens on the copy the store holds; writes keep it in sync.
 */
const SubjectTypeEditContent: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { subjectTypeId: routeSubjectTypeId } = useParams();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const references = useSubjectTypeEditorReferences(accountId);

	const isNew = routeSubjectTypeId === undefined;
	const subjectTypeId = isNew ? null : Number(routeSubjectTypeId);
	const hasValidId = isNew || Number.isInteger(subjectTypeId);

	const [fetchFailure, setFetchFailure] = React.useState<FetchFailure | null>(null);

	const {
		data: definition,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.definition,
		stopOn: (value) =>
			accountId === null || subjectTypeId === null || !hasValidId || value?.id === subjectTypeId,
		query: () => {
			if (accountId === null || subjectTypeId === null || !hasValidId) {
				return Promise.resolve();
			}

			return dispatch(
				fetchSubjectTypeDefinition(new FetchSubjectTypeDefinition(accountId, subjectTypeId)),
			)
				.then(() => {
					setFetchFailure(null);
				})
				.catch((rejection: unknown) => {
					if (isFetchFailure(rejection)) {
						setFetchFailure({
							subjectTypeId,
							isNotFound: rejection.payload?.status === 404,
							message: rejection.payload?.message ?? null,
						});
					}

					throw rejection;
				});
		},
		deps: [accountId, subjectTypeId],
	});

	const handleBackClick = React.useCallback(() => {
		navigate(LIST_PATH);
	}, [navigate]);

	const isNotFound =
		!hasValidId || (fetchFailure?.isNotFound === true && fetchFailure.subjectTypeId === subjectTypeId);

	if (isNotFound) {
		return (
			<EmptyState
				title="Ce type de dossier est introuvable."
				icon={<SearchX size={28} />}
				action={
					<Button type="button" label="Retour aux types de dossier" onClick={handleBackClick} />
				}
			/>
		);
	}

	const hasFailed = error !== null && definition?.id !== subjectTypeId;

	if (hasFailed) {
		return (
			<ErrorState
				title="Impossible de charger le type de dossier"
				description={fetchFailure?.message ?? DEFAULT_LOAD_ERROR_MESSAGE}
				onRetry={refetch}
			/>
		);
	}

	if (references.hasFailed) {
		return (
			<ErrorState
				title="Impossible de charger les données de l'éditeur"
				description="Les workflows et les listes de choix n'ont pas pu être lus."
				onRetry={references.refetch}
			/>
		);
	}

	const isDefinitionLoaded = isNew || definition?.id === subjectTypeId;
	const isLoading = !isDefinitionLoaded || !references.isReady;

	if (isLoading) {
		return (
			<div css={skeletonStyle}>
				<Skeleton height="6rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	const editedDefinition = isNew ? null : definition;

	return (
		<SubjectTypeEditor
			key={`${editedDefinition?.id ?? 'new'}:${editedDefinition?.archivedAt ?? ''}`}
			definition={editedDefinition}
		/>
	);
};

export default SubjectTypeEditContent;
